/**
 * TopBar component - uses real market data from WebSocket/API
 * NO MOCK DATA - all prices come from Polygon.io
 */
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Bell,
  Menu,
  User,
  Settings,
  LogOut,
  TrendingUp,
  TrendingDown,
  Minus,
  Loader2,
  Moon,
  Sun
} from 'lucide-react'
import clsx from 'clsx'
import { useUIStore } from '../../stores/uiStore'
import { useAuthStore } from '../../stores/authStore'
import { useMarketStatus } from '../../hooks/useMarketStatus'
import { useTheme } from '../../hooks/useTheme'
import { IconButton } from '../ui/Button'
import { GlossaryHelpButton, useGlobalGlossary, GlobalGlossary } from '../ui/GlobalGlossary'
import { CommandPalette } from '../CommandPalette'
import { SearchBar } from '../SearchBar'
import { alertsApi } from '../../api/alerts'
import './TopBar.css'

interface MarketIndexData {
  symbol: string;
  value: number;
  change: number;
  changePercent: number;
}

export function TopBar() {
  const navigate = useNavigate()
  const { toggleSidebar, commandPaletteOpen, closeCommandPalette, openCommandPalette } = useUIStore()
  const { user, logout } = useAuthStore()
  const marketStatus = useMarketStatus()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const glossary = useGlobalGlossary()
  const { resolvedTheme, toggleTheme } = useTheme()

  // Fetch unread alert count
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['alerts', 'unreadCount'],
    queryFn: alertsApi.getUnreadCount,
    refetchInterval: 30000, // Refetch every 30 seconds
    staleTime: 10000
  })

  // Fetch recent alert events for notifications dropdown
  const { data: recentEvents = [] } = useQuery({
    queryKey: ['alerts', 'recent'],
    queryFn: () => alertsApi.getAlertEvents({ limit: 5, offset: 0 }),
    enabled: showNotifications,
    staleTime: 10000
  })

  // Ctrl+K keyboard shortcut to open command palette
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        openCommandPalette()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [openCommandPalette])

  // Build indices data from market status
  const indices: MarketIndexData[] = [
    {
      symbol: 'SPY',
      value: marketStatus.spyPrice || 0,
      change: marketStatus.spyChange || 0,
      changePercent: marketStatus.spyChange || 0
    },
    {
      symbol: 'VIX',
      value: marketStatus.vix || 0,
      change: marketStatus.vixChange || 0,
      changePercent: marketStatus.vixChange || 0
    }
  ]

  return (
    <header className="topbar">
      <div className="topbar-left">
        <IconButton
          label="Toggle menu"
          onClick={toggleSidebar}
          className="topbar-menu-btn"
        >
          <Menu size={20} />
        </IconButton>

        {/* Unified SearchBar - always visible */}
        <SearchBar />
      </div>

      <div className="topbar-center">
        <MarketTicker
          isOpen={marketStatus.isOpen}
          indices={indices}
          isLoading={!marketStatus.isConnected && marketStatus.spyPrice === 0}
        />
      </div>

      <div className="topbar-right">
        <IconButton
          label={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          onClick={toggleTheme}
          className="topbar-theme-toggle"
        >
          {resolvedTheme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
        </IconButton>

        <GlossaryHelpButton onClick={glossary.open} />

        <div className="topbar-notifications-wrapper">
          <IconButton
            label="Notifications"
            className="topbar-notifications"
            onClick={() => setShowNotifications(!showNotifications)}
          >
            <Bell size={20} />
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
            )}
          </IconButton>

          {showNotifications && (
            <>
              <div className="notifications-backdrop" onClick={() => setShowNotifications(false)} />
              <div className="notifications-dropdown">
                <div className="notifications-header">
                  <span>Notifications</span>
                  {unreadCount > 0 && (
                    <button
                      className="notifications-mark-all"
                      onClick={() => {
                        alertsApi.markAllRead()
                        setShowNotifications(false)
                      }}
                    >
                      Mark all read
                    </button>
                  )}
                </div>
                <div className="notifications-list">
                  {recentEvents.length === 0 ? (
                    <div className="notifications-empty">
                      <Bell size={24} />
                      <span>No notifications yet</span>
                    </div>
                  ) : (
                    recentEvents.map((event) => (
                      <button
                        key={event.id}
                        className={clsx('notification-item', !event.is_read && 'notification-unread')}
                        onClick={() => {
                          navigate('/alerts')
                          setShowNotifications(false)
                        }}
                      >
                        <div className="notification-content">
                          <span className="notification-title">{event.brief_title || event.message}</span>
                          <span className="notification-ticker">{event.ticker}</span>
                        </div>
                        {!event.is_read && <span className="notification-dot" />}
                      </button>
                    ))
                  )}
                </div>
                <button
                  className="notifications-view-all"
                  onClick={() => {
                    navigate('/alerts')
                    setShowNotifications(false)
                  }}
                >
                  View all alerts
                </button>
              </div>
            </>
          )}
        </div>

        <div className="topbar-user">
          <button
            className="topbar-user-btn"
            onClick={() => setShowUserMenu(!showUserMenu)}
            aria-label="User menu"
            aria-expanded={showUserMenu}
          >
            <div className="topbar-avatar">
              {user?.email?.charAt(0).toUpperCase() || 'U'}
            </div>
          </button>

          {showUserMenu && (
            <div className="topbar-user-menu">
              <div className="user-menu-header">
                <span className="user-menu-name">{user?.email || 'User'}</span>
                <span className="user-menu-role">Pro Account</span>
              </div>
              <div className="user-menu-divider" />
              <button className="user-menu-item" onClick={() => { navigate('/profile'); setShowUserMenu(false); }}>
                <User size={16} />
                <span>Profile</span>
              </button>
              <button className="user-menu-item" onClick={() => { navigate('/settings'); setShowUserMenu(false); }}>
                <Settings size={16} />
                <span>Settings</span>
              </button>
              <div className="user-menu-divider" />
              <button className="user-menu-item user-menu-item-danger" onClick={logout}>
                <LogOut size={16} />
                <span>Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Command Palette */}
      <CommandPalette
        isOpen={commandPaletteOpen}
        onClose={closeCommandPalette}
      />

      {/* Global Glossary */}
      <GlobalGlossary isOpen={glossary.isOpen} onClose={glossary.close} />
    </header>
  )
}

interface MarketTickerProps {
  isOpen: boolean;
  indices: MarketIndexData[];
  isLoading?: boolean;
}

function MarketTicker({ isOpen, indices, isLoading }: MarketTickerProps) {
  // Add timeout to prevent perpetual loading state
  const [loadingTimeout, setLoadingTimeout] = useState(false)

  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => setLoadingTimeout(true), 10000) // 10 second timeout
      return () => clearTimeout(timer)
    } else {
      setLoadingTimeout(false)
    }
  }, [isLoading])

  // Hide loading indicator after timeout
  const showLoading = isLoading && !loadingTimeout

  return (
    <div className="market-ticker">
      <div className={clsx('market-status', isOpen ? 'market-open' : 'market-closed')}>
        <span className="market-status-dot" />
        <span className="market-status-text">
          {isOpen ? 'Market Open' : 'Market Closed'}
        </span>
      </div>

      <div className="market-indices">
        {showLoading ? (
          <div className="market-loading">
            <Loader2 size={14} className="animate-spin" />
            <span>Loading...</span>
          </div>
        ) : (
          indices.filter(idx => idx.value > 0).map((index) => (
            <div key={index.symbol} className="market-index">
              <span className="index-symbol">{index.symbol}</span>
              <span className="index-value">{index.value.toFixed(2)}</span>
              <span className={clsx(
                'index-change',
                index.change > 0 && 'index-change-up',
                index.change < 0 && 'index-change-down'
              )}>
                {index.change > 0 ? (
                  <TrendingUp size={12} />
                ) : index.change < 0 ? (
                  <TrendingDown size={12} />
                ) : (
                  <Minus size={12} />
                )}
                {index.change > 0 ? '+' : ''}{index.changePercent.toFixed(2)}%
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
